import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, Copy } from "lucide-react";

const gbp = (n) => "£" + Number(n || 0).toFixed(2);

const CONTENT_CHOICES = [
  { k: "before_photo", label: "Before photo" },
  { k: "after_photo", label: "After photo" },
  { k: "video", label: "Video" },
  { k: "social_media", label: "Social media" },
  { k: "instagram_story", label: "Instagram Story" },
  { k: "instagram_reel", label: "Instagram Reel" },
  { k: "other", label: "Other/custom" },
];

const OFFER_STATUSES = ["Draft", "Active", "Paused", "Expired", "Archived"];

export default function AdminModels() {
  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <h2 className="font-serif text-4xl font-light">Model Days</h2>
        <p className="text-xs text-stone-500 ml-2">Book model appointments and generate shareable public links</p>
      </div>
      <ModelDaysPanel />
    </div>
  );
}

function ModelDaysPanel() {
  const [days, setDays] = useState([]);
  const [editing, setEditing] = useState(null); // day object or "new"
  const load = () => api.get("/admin/model/days").then((r) => setDays(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[11px] text-stone-500">
            Each Model Day owns its date + treatments + time slots. Booking link goes to anyone —
            no email verification. Slots create real appointments with consent forms + reminders.
          </p>
        </div>
        <button onClick={() => setEditing("new")} data-testid="create-model-day-btn"
                className="border border-stone-900 px-4 py-2 mono-label hover:bg-stone-900 hover:text-white transition flex items-center gap-2">
          <Plus size={14} /> Create Model Day
        </button>
      </div>
      <div className="border hairline bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-alt mono-label">
            <tr>
              <th className="text-left p-3">Date</th>
              <th className="text-left p-3">Title</th>
              <th className="text-left p-3">Window</th>
              <th className="text-left p-3">Treatments</th>
              <th className="text-right p-3">Slots</th>
              <th className="text-right p-3">Available</th>
              <th className="text-right p-3">Booked</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {days.map((d) => (
              <tr key={d.id} className="border-t hairline row-hover cursor-pointer" data-testid={`model-day-row-${d.id}`}
                  onClick={() => setEditing(d)}>
                <td className="p-3 font-medium">{d.date}</td>
                <td className="p-3">{d.title || "—"}</td>
                <td className="p-3 mono-label text-[10px] text-stone-500">
                  {d.start_time && d.end_time ? `${d.start_time}–${d.end_time}` : "—"}
                </td>
                <td className="p-3 text-xs text-stone-600">
                  {(d.treatments || []).map((t) => t.name).slice(0, 3).join(" · ") || "—"}
                  {(d.treatments || []).length > 3 && ` +${d.treatments.length - 3}`}
                </td>
                <td className="p-3 text-right">{d.counts?.total || 0}</td>
                <td className="p-3 text-right text-emerald-700">{d.counts?.available || 0}</td>
                <td className="p-3 text-right">{d.counts?.booked || 0}</td>
                <td className="p-3 mono-label text-[10px] text-stone-500">MANAGE</td>
              </tr>
            ))}
          </tbody>
        </table>
        {days.length === 0 && <p className="p-6 text-stone-500 text-sm">No Model Days yet.</p>}
      </div>
      {editing && (
        <ModelDayEditor
          dayId={editing === "new" ? null : editing.id}
          onClose={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function ModelDayEditor({ dayId, onClose }) {
  const isNew = !dayId;
  const [treatments, setTreatments] = useState([]);
  const [clients, setClients] = useState([]);
  const [day, setDay] = useState({
    date: "", title: "", note: "",
    start_time: "10:00", end_time: "17:00",
    treatment_ids: [], slots: [],
  });
  const [loaded, setLoaded] = useState(null); // full detail from server (edit mode)
  const [newSlot, setNewSlot] = useState({ time: "", treatment_id: "", duration_min: "", model_price: "", deposit_amount: "" });
  const [assignFor, setAssignFor] = useState(null); // slot id being assigned
  const [addClientMode, setAddClientMode] = useState(false); // top-level "Add Existing Client" flow
  const [pickedClient, setPickedClient] = useState(null); // client selected in top-level flow
  const [clientQ, setClientQ] = useState("");
  const [publicLink, setPublicLink] = useState("");

  useEffect(() => {
    api.get("/treatments/all").then((r) => setTreatments(r.data.filter((t) => t.active)));
    api.get("/clients").then((r) => setClients(r.data));
    if (dayId) {
      api.get(`/admin/model/days/${dayId}`).then((r) => {
        setLoaded(r.data);
        setDay({
          date: r.data.date,
          title: r.data.title || "",
          note: r.data.note || "",
          start_time: r.data.start_time || "10:00",
          end_time: r.data.end_time || "17:00",
          treatment_ids: r.data.treatment_ids || [],
          slots: [],
        });
        setPublicLink(`${window.location.origin}/model-day/${r.data.public_token}`);
      });
    }
  }, [dayId]);

  const toggleTreatment = (tid) => {
    setDay((d) => ({
      ...d,
      treatment_ids: d.treatment_ids.includes(tid)
        ? d.treatment_ids.filter((x) => x !== tid)
        : [...d.treatment_ids, tid],
    }));
  };

  const addDraftSlot = () => {
    if (!newSlot.time || !newSlot.treatment_id) { toast.error("Time and treatment required"); return; }
    setDay((d) => ({
      ...d,
      slots: [...d.slots, {
        _draft: true, id: `draft-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        time: newSlot.time, treatment_id: newSlot.treatment_id,
        duration_min: newSlot.duration_min ? Number(newSlot.duration_min) : null,
        model_price: Number(newSlot.model_price || 0),
        deposit_amount: Number(newSlot.deposit_amount || 0),
        status: "AVAILABLE",
      }],
    }));
    setNewSlot({ time: "", treatment_id: "", duration_min: "", model_price: "", deposit_amount: "" });
  };

  const removeDraftSlot = (s) => {
    setDay((d) => ({ ...d, slots: d.slots.filter((x) => x !== s) }));
  };

  const addPersistedSlot = async () => {
    if (!newSlot.time || !newSlot.treatment_id) { toast.error("Time and treatment required"); return; }
    try {
      await api.post(`/admin/model/days/${dayId}/slots`, {
        time: newSlot.time,
        treatment_id: newSlot.treatment_id,
        duration_min: newSlot.duration_min ? Number(newSlot.duration_min) : null,
        model_price: Number(newSlot.model_price || 0),
        deposit_amount: Number(newSlot.deposit_amount || 0),
      });
      setNewSlot({ time: "", treatment_id: "", duration_min: "", model_price: "", deposit_amount: "" });
      const r = await api.get(`/admin/model/days/${dayId}`);
      setLoaded(r.data);
      toast.success("Slot added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add slot"); }
  };

  const updateSlotDeposit = async (slot, newDeposit) => {
    try {
      await api.patch(`/admin/model/days/${dayId}/slots/${slot.id}`, {
        time: slot.time,
        treatment_id: slot.treatment_id,
        duration_min: slot.duration_min || null,
        model_price: Number(slot.model_price || 0),
        deposit_amount: Number(newDeposit || 0),
      });
      const r = await api.get(`/admin/model/days/${dayId}`);
      setLoaded(r.data);
      toast.success("Deposit updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const deletePersistedSlot = async (sid) => {
    if (!window.confirm("Delete this slot?")) return;
    try {
      await api.delete(`/admin/model/days/${dayId}/slots/${sid}`);
      const r = await api.get(`/admin/model/days/${dayId}`);
      setLoaded(r.data);
      toast.success("Slot deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const assignExistingClient = async (slot, client) => {
    try {
      await api.post(`/admin/model/days/${dayId}/assign-existing`, {
        slot_id: slot.id, client_id: client.id,
      });
      toast.success(`${client.first_name} ${client.last_name} booked into ${slot.time}`);
      setAssignFor(null); setClientQ("");
      setAddClientMode(false); setPickedClient(null);
      const r = await api.get(`/admin/model/days/${dayId}`);
      setLoaded(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Assign failed"); }
  };

  const sendBookingLink = async (client) => {
    try {
      const r = await api.post(`/admin/model/days/${dayId}/send-booking-link`, {
        client_id: client.id,
      });
      toast.success(`Booking link sent to ${r.data.recipient}`);
      setAddClientMode(false); setPickedClient(null); setClientQ("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send booking link");
    }
  };

  const create = async () => {
    if (!day.date) { toast.error("Pick a date"); return; }
    try {
      const r = await api.post("/admin/model/days", {
        date: day.date,
        title: day.title || null,
        note: day.note || null,
        start_time: day.start_time || null,
        end_time: day.end_time || null,
        treatment_ids: day.treatment_ids,
        slots: day.slots.map(({ time, treatment_id, duration_min, model_price, deposit_amount }) => ({
          time, treatment_id, duration_min, model_price, deposit_amount: deposit_amount || 0,
        })),
      });
      if (r.data.public_bookings_on_this_date > 0) {
        toast.warning(`⚠ ${r.data.public_bookings_on_this_date} public booking(s) already exist on this date. They remain active but the calendar is now closed for further public bookings.`, { duration: 8000 });
      } else {
        toast.success("Model Day created");
      }
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
  };

  const savePatch = async () => {
    try {
      await api.patch(`/admin/model/days/${dayId}`, {
        title: day.title || null,
        note: day.note || null,
        start_time: day.start_time || null,
        end_time: day.end_time || null,
        treatment_ids: day.treatment_ids,
      });
      toast.success("Model Day updated");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const cancelDay = async () => {
    if (!window.confirm("Cancel this Model Day? The date will reopen for public bookings. Booked appointments will NOT be cancelled — cancel them from Admin → Calendar first if needed.")) return;
    try {
      await api.delete(`/admin/model/days/${dayId}`);
      toast.success("Model Day cancelled — date reopened");
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.booked_count) {
        if (window.confirm(detail.message + "\n\nProceed?")) {
          await api.delete(`/admin/model/days/${dayId}?force=true`);
          toast.success(`Cancelled — ${detail.booked_count} booked appointment(s) kept`);
          onClose();
        }
      } else {
        toast.error(detail || "Failed");
      }
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicLink);
      toast.success("Public booking link copied");
    } catch { toast.error("Could not copy — please copy manually"); }
  };

  const persistedSlots = loaded?.slots || [];
  const displaySlots = isNew ? day.slots : persistedSlots;
  const filteredClients = useMemo(() => {
    const q = clientQ.toLowerCase().trim();
    if (!q) return clients.slice(0, 25);
    return clients.filter((c) => `${c.first_name} ${c.last_name} ${c.email} ${c.phone}`.toLowerCase().includes(q)).slice(0, 25);
  }, [clients, clientQ]);

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/40" onClick={onClose}>
      <div className="absolute right-0 top-0 h-full w-full sm:max-w-3xl bg-white overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-8">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="mono-label text-[10px] text-stone-400">MODEL DAY</p>
              <h3 className="font-serif text-3xl mt-1" data-testid="model-day-editor-title">
                {isNew ? "New Model Day" : `${day.date}${day.title ? ` · ${day.title}` : ""}`}
              </h3>
            </div>
            <button onClick={onClose}><X /></button>
          </div>

          {/* Public link banner */}
          {!isNew && publicLink && (
            <div className="mt-2 mb-4 border hairline bg-stone-50 p-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="mono-label text-[10px] text-stone-500">PUBLIC MODEL BOOKING LINK</p>
                <p className="text-xs text-stone-800 truncate mt-1" data-testid="public-link">{publicLink}</p>
              </div>
              <button onClick={copyLink} data-testid="copy-public-link"
                      className="border border-stone-900 mono-label text-xs px-3 py-2 hover:bg-stone-900 hover:text-white transition flex items-center gap-1 shrink-0">
                <Copy size={12} /> COPY LINK
              </button>
            </div>
          )}

          {/* Add Existing Client top-level flow */}
          {!isNew && (
            <div className="mb-4 border hairline bg-white p-3">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="mono-label text-[10px] text-stone-500">EXISTING CLIENT</p>
                  <p className="text-xs text-stone-600 mt-1">
                    Book an existing CLAÈ client in yourself, or email them the booking link so they can pick their own slot.
                  </p>
                </div>
                {!addClientMode ? (
                  <button onClick={() => { setAddClientMode(true); setPickedClient(null); setClientQ(""); }}
                          data-testid="add-existing-client-btn"
                          className="border border-stone-900 mono-label text-xs px-3 py-2 hover:bg-stone-900 hover:text-white transition flex items-center gap-1 shrink-0">
                    <Plus size={12} /> ADD EXISTING CLIENT
                  </button>
                ) : (
                  <button onClick={() => { setAddClientMode(false); setPickedClient(null); setClientQ(""); }}
                          className="mono-label text-[10px] text-stone-500 hover:text-stone-900 shrink-0">
                    CANCEL
                  </button>
                )}
              </div>
              {addClientMode && !pickedClient && (
                <div className="mt-3 border-t hairline pt-3">
                  <input type="text" placeholder="Search by name, email or phone…"
                         data-testid="add-client-search" value={clientQ}
                         onChange={(e) => setClientQ(e.target.value)}
                         className="w-full border hairline p-2 text-sm" autoFocus />
                  <div className="mt-2 max-h-64 overflow-y-auto border hairline">
                    {filteredClients.map((c) => (
                      <button key={c.id} onClick={() => setPickedClient(c)}
                              data-testid={`pick-client-${c.id}`}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-alt border-b hairline flex items-center justify-between">
                        <span>{c.first_name} {c.last_name}</span>
                        <span className="text-xs text-stone-500">{c.email} · {c.phone || "no phone"}</span>
                      </button>
                    ))}
                    {filteredClients.length === 0 && (
                      <p className="text-xs text-stone-500 p-3">No clients matched.</p>
                    )}
                  </div>
                </div>
              )}
              {addClientMode && pickedClient && (
                <div className="mt-3 border-t hairline pt-3" data-testid="add-client-actions">
                  <p className="text-sm">
                    <span className="mono-label text-[10px] text-stone-500 mr-2">SELECTED</span>
                    <b>{pickedClient.first_name} {pickedClient.last_name}</b> · {pickedClient.email}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <button onClick={() => sendBookingLink(pickedClient)} data-testid="send-booking-link"
                            className="mono-label text-xs border border-stone-900 px-3 py-2 hover:bg-stone-900 hover:text-white transition">
                      SEND BOOKING LINK
                    </button>
                    <button onClick={() => setPickedClient(null)}
                            className="mono-label text-[10px] text-stone-500 hover:text-stone-900">
                      CHANGE CLIENT
                    </button>
                  </div>
                  <p className="mono-label text-[10px] text-stone-500 mt-4 mb-2">OR BOOK NOW — PICK A SLOT</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 max-h-56 overflow-y-auto">
                    {(loaded?.slots || []).filter((s) => s.status === "AVAILABLE").map((s) => (
                      <button key={s.id} onClick={() => assignExistingClient(s, pickedClient)}
                              data-testid={`add-client-book-${s.id}`}
                              className="border hairline p-2 text-left text-xs hover:border-stone-900 hover:bg-alt">
                        <div className="font-medium">{s.time} · {s.treatment_name}</div>
                        <div className="mono-label text-[10px] text-stone-500 mt-1">
                          MODEL {gbp(s.model_price)}{s.deposit_amount > 0 ? ` · DEP ${gbp(s.deposit_amount)}` : ""}
                        </div>
                      </button>
                    ))}
                    {(loaded?.slots || []).filter((s) => s.status === "AVAILABLE").length === 0 && (
                      <p className="text-xs text-stone-500 col-span-full">No available slots.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Day details */}
          <div className="border-t hairline pt-4">
            <p className="mono-label mb-3">Date & window</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <label className="text-xs text-stone-600">
                <span className="mono-label text-[10px] text-stone-500">Date *</span>
                <input type="date" data-testid="md-date" value={day.date} disabled={!isNew}
                       onChange={(e) => setDay({ ...day, date: e.target.value })}
                       className="w-full border hairline p-2 text-sm mt-1 disabled:bg-stone-100" />
              </label>
              <label className="text-xs text-stone-600">
                <span className="mono-label text-[10px] text-stone-500">Start</span>
                <input type="time" data-testid="md-start" value={day.start_time}
                       onChange={(e) => setDay({ ...day, start_time: e.target.value })}
                       className="w-full border hairline p-2 text-sm mt-1" />
              </label>
              <label className="text-xs text-stone-600">
                <span className="mono-label text-[10px] text-stone-500">End</span>
                <input type="time" data-testid="md-end" value={day.end_time}
                       onChange={(e) => setDay({ ...day, end_time: e.target.value })}
                       className="w-full border hairline p-2 text-sm mt-1" />
              </label>
              <label className="text-xs text-stone-600 col-span-2 md:col-span-4">
                <span className="mono-label text-[10px] text-stone-500">Title (optional)</span>
                <input type="text" data-testid="md-title" value={day.title}
                       placeholder="e.g. October Model Day — Injectables"
                       onChange={(e) => setDay({ ...day, title: e.target.value })}
                       className="w-full border hairline p-2 text-sm mt-1" />
              </label>
              <label className="text-xs text-stone-600 col-span-2 md:col-span-4">
                <span className="mono-label text-[10px] text-stone-500">Note (optional)</span>
                <textarea data-testid="md-note" value={day.note} rows={2}
                          onChange={(e) => setDay({ ...day, note: e.target.value })}
                          className="w-full border hairline p-2 text-sm mt-1" />
              </label>
            </div>
          </div>

          {/* Allowed treatments */}
          <div className="mt-6 border-t hairline pt-4">
            <p className="mono-label mb-3">Treatments on this day</p>
            <p className="text-[11px] text-stone-500 mb-3">Only these treatments can be assigned to slots. Each slot has its own model price (admin editable).</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-64 overflow-y-auto border hairline p-2">
              {treatments.map((t) => (
                <label key={t.id} className="flex items-center gap-2 p-1 text-sm cursor-pointer">
                  <input type="checkbox" data-testid={`md-treatment-${t.id}`}
                         checked={day.treatment_ids.includes(t.id)}
                         onChange={() => toggleTreatment(t.id)} />
                  <span className="flex-1">{t.name}</span>
                  <span className="text-stone-400 text-[10px]">{gbp(t.price)}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Slots */}
          <div className="mt-6 border-t hairline pt-4">
            <p className="mono-label mb-3">Slots ({displaySlots.length})</p>
            <p className="text-[11px] text-stone-500 mb-3">Add every appointment window. Each slot has a start time, treatment, duration and its own model price.</p>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end mb-3">
              <label className="text-xs text-stone-600">
                <span className="mono-label text-[10px] text-stone-500">Time</span>
                <input type="time" data-testid="md-newslot-time" value={newSlot.time}
                       onChange={(e) => setNewSlot({ ...newSlot, time: e.target.value })}
                       className="w-full border hairline p-2 text-sm mt-1" />
              </label>
              <label className="text-xs text-stone-600 col-span-2">
                <span className="mono-label text-[10px] text-stone-500">Treatment</span>
                <select data-testid="md-newslot-treatment" value={newSlot.treatment_id}
                        onChange={(e) => setNewSlot({ ...newSlot, treatment_id: e.target.value })}
                        className="w-full border hairline p-2 text-sm mt-1 bg-white">
                  <option value="">— Select —</option>
                  {(day.treatment_ids.length ? treatments.filter((t) => day.treatment_ids.includes(t.id)) : treatments).map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </label>
              <label className="text-xs text-stone-600">
                <span className="mono-label text-[10px] text-stone-500">Duration (min)</span>
                <input type="number" min="15" step="15" placeholder="default" data-testid="md-newslot-duration"
                       value={newSlot.duration_min}
                       onChange={(e) => setNewSlot({ ...newSlot, duration_min: e.target.value })}
                       className="w-full border hairline p-2 text-sm mt-1" />
              </label>
              <label className="text-xs text-stone-600">
                <span className="mono-label text-[10px] text-stone-500">Model price £</span>
                <input type="number" min="0" step="1" data-testid="md-newslot-price"
                       value={newSlot.model_price}
                       onChange={(e) => setNewSlot({ ...newSlot, model_price: e.target.value })}
                       className="w-full border hairline p-2 text-sm mt-1" />
              </label>
              <label className="text-xs text-stone-600" title="Amount charged upfront via Stripe on the booking link. Balance is settled in clinic. £0 = full amount in clinic (no Stripe checkout).">
                <span className="mono-label text-[10px] text-stone-500">Deposit £</span>
                <input type="number" min="0" step="1" data-testid="md-newslot-deposit"
                       value={newSlot.deposit_amount}
                       onChange={(e) => setNewSlot({ ...newSlot, deposit_amount: e.target.value })}
                       className="w-full border hairline p-2 text-sm mt-1" />
              </label>
            </div>
            <button onClick={isNew ? addDraftSlot : addPersistedSlot} data-testid="md-add-slot"
                    className="border border-stone-900 px-3 py-2 mono-label text-xs hover:bg-stone-900 hover:text-white flex items-center gap-1">
              <Plus size={12} /> ADD SLOT
            </button>

            <ul className="mt-4 space-y-2">
              {displaySlots.map((s) => {
                const t = treatments.find((x) => x.id === s.treatment_id);
                const client = s.booked_client;
                return (
                  <li key={s.id} data-testid={`slot-row-${s.id}`}
                      className={`border hairline p-3 ${s.status === "BOOKED" ? "bg-emerald-50" : "bg-white"}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium">{s.time} · {s.treatment_name || t?.name || "—"}</p>
                        <p className="mono-label text-[10px] text-stone-500 mt-1">
                          {s.duration_min || "—"} min · MODEL {gbp(s.model_price)}
                          {s.deposit_amount > 0 ? ` · DEP ${gbp(s.deposit_amount)} · IN-CLINIC ${gbp(s.model_price - s.deposit_amount)}` : " · ALL IN-CLINIC"}
                        </p>
                        {!isNew && s.status === "AVAILABLE" && (
                          <div className="mt-2 flex items-center gap-2">
                            <span className="mono-label text-[10px] text-stone-400">DEPOSIT £</span>
                            <input
                              type="number" min="0" step="1" defaultValue={s.deposit_amount || 0}
                              data-testid={`slot-deposit-${s.id}`}
                              onBlur={(e) => {
                                const v = Number(e.target.value || 0);
                                if (v !== Number(s.deposit_amount || 0)) updateSlotDeposit(s, v);
                              }}
                              className="w-24 border hairline p-1 text-xs"
                            />
                          </div>
                        )}
                        {client && (
                          <p className="text-xs text-emerald-900 mt-1" data-testid={`slot-client-${s.id}`}>
                            {client.client_name} · {client.client_email} · Ref {client.reference}
                            {client.consent_email_sent && <span className="ml-2 mono-label text-[10px] text-emerald-700">CONSENT SENT</span>}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`mono-label text-[10px] px-2 py-0.5 border ${
                          s.status === "BOOKED" ? "text-emerald-700 border-emerald-300"
                          : s.status === "CANCELLED" ? "text-red-700 border-red-300"
                          : "text-stone-600 border-stone-300"
                        }`}>{s.status}</span>
                        {!isNew && s.status === "AVAILABLE" && (
                          <button onClick={() => setAssignFor(s.id)} data-testid={`assign-existing-${s.id}`}
                                  className="mono-label text-[10px] border hairline px-2 py-1 hover:bg-alt">
                            ASSIGN EXISTING
                          </button>
                        )}
                        {isNew ? (
                          <button onClick={() => removeDraftSlot(s)} className="text-red-600" data-testid={`remove-draft-${s.id}`}><Trash2 size={14} /></button>
                        ) : (
                          s.status !== "BOOKED" && (
                            <button onClick={() => deletePersistedSlot(s.id)} className="text-red-600" data-testid={`delete-slot-${s.id}`}><Trash2 size={14} /></button>
                          )
                        )}
                      </div>
                    </div>
                    {assignFor === s.id && (
                      <div className="mt-3 pt-3 border-t hairline">
                        <input type="text" placeholder="Search clients by name, email or phone…"
                               data-testid={`client-search-${s.id}`}
                               value={clientQ} onChange={(e) => setClientQ(e.target.value)}
                               className="w-full border hairline p-2 text-sm" />
                        <div className="mt-2 max-h-48 overflow-y-auto border hairline">
                          {filteredClients.map((c) => (
                            <button key={c.id} onClick={() => assignExistingClient(s, c)}
                                    data-testid={`assign-client-${c.id}`}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-alt border-b hairline flex items-center justify-between">
                              <span>{c.first_name} {c.last_name}</span>
                              <span className="text-xs text-stone-500">{c.email}</span>
                            </button>
                          ))}
                          {filteredClients.length === 0 && (
                            <p className="text-xs text-stone-500 p-3">No clients matched.</p>
                          )}
                        </div>
                        <button onClick={() => { setAssignFor(null); setClientQ(""); }}
                                className="mt-2 mono-label text-[10px] text-stone-500 hover:text-stone-900">
                          CANCEL
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
              {displaySlots.length === 0 && <li className="text-xs text-stone-500">No slots yet.</li>}
            </ul>
          </div>

          <div className="mt-8 flex gap-2 sticky bottom-0 bg-white pt-2 pb-2 border-t hairline">
            {!isNew && (
              <button onClick={cancelDay} data-testid="cancel-model-day"
                      className="mono-label text-xs border border-red-300 text-red-700 px-3 py-2 hover:bg-red-50">
                CANCEL DAY
              </button>
            )}
            <button onClick={onClose} className="ml-auto mono-label border hairline px-4 py-3">CLOSE</button>
            <button onClick={isNew ? create : savePatch} data-testid="save-model-day"
                    className="mono-label bg-stone-900 text-white px-6 py-3 hover:bg-[#C5A059] transition"
                    style={{ color: "#fff" }}>
              {isNew ? "CREATE MODEL DAY" : "SAVE"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
